import { trace, context, SpanStatusCode } from "@opentelemetry/api";
import axios from "./axiosInstance";

const pendingControllers = new Map();

export const createAbortController = (key) => {
  pendingControllers.get(key)?.abort();
  const controller = new AbortController();
  pendingControllers.set(key, controller);
  return controller;
};

export const cleanupAbortController = (key, controller) => {
  if (pendingControllers.get(key) === controller) {
    pendingControllers.delete(key);
  }
};

const convertBlobToText = async (err) => {
  return JSON.parse(await err?.response?.data?.text());
};

export const postCall = async (url, data, params = {}, errorCallback = "") => {
  const tracer = trace.getTracer("oms-ui");
  const span = tracer.startSpan(`postCall:${url}`, {
    attributes: { "http.url": url, "http.method": "POST" },
  });

  return await context.with(trace.setSpan(context.active(), span), async () => {
    try {
      const response = await axios.post(url, data, {
        params,
      });
      span.setStatus({ code: SpanStatusCode.OK });
      return response.data;
    } catch (err) {
      span.recordException(err);
      span.setStatus({
        code: SpanStatusCode.ERROR,
        message: err.message,
      });
      errorCallback && errorCallback(err?.response);
    } finally {
      span.end();
    }
  });
};

export const putCall = async (url, data, params = {}, errorCallback = "") => {
  const tracer = trace.getTracer("oms-ui");
  const span = tracer.startSpan(`putCall:${url}`, {
    attributes: { "http.url": url, "http.method": "PUT" },
  });

  return await context.with(trace.setSpan(context.active(), span), async () => {
    try {
      const response = await axios.put(url, data, {
        params,
      });
      span.setStatus({ code: SpanStatusCode.OK });
      return response.data;
    } catch (err) {
      span.recordException(err);
      span.setStatus({
        code: SpanStatusCode.ERROR,
        message: err.message,
      });
      errorCallback && errorCallback(err?.response);
    } finally {
      span.end();
    }
  });
};

export const getCall = async (
  url,
  params = {},
  errorCallback = "",
  responseType = "json",
) => {
  const tracer = trace.getTracer("oms-ui");
  const span = tracer.startSpan(`getCall:${url}`, {
    attributes: { "http.url": url, "http.method": "GET" },
  });

  return await context.with(trace.setSpan(context.active(), span), async () => {
    try {
      const response = await axios.get(url, {
        params,
        responseType,
      });
      span.setStatus({ code: SpanStatusCode.OK });
      return response.data;
    } catch (err) {
      span.recordException(err);
      span.setStatus({
        code: SpanStatusCode.ERROR,
        message: err.message,
      });
      if (!errorCallback) return;

      if (responseType === "blob") {
        errorCallback(await convertBlobToText(err));
      } else {
        errorCallback(err?.response);
      }
    } finally {
      span.end();
    }
  });
};

export const getCall_v2 = async (url, params = {}, responseType = "json") => {
  const tracer = trace.getTracer("oms-ui");
  const span = tracer.startSpan(`getCall_v2:${url}`, {
    attributes: { "http.url": url, "http.method": "GET" },
  });

  return await context.with(trace.setSpan(context.active(), span), async () => {
    try {
      const response = await axios.get(url, {
        params,
        responseType,
      });
      span.setStatus({ code: SpanStatusCode.OK });
      return response;
    } catch (err) {
      span.recordException(err);
      span.setStatus({
        code: SpanStatusCode.ERROR,
        message: err.message,
      });
      if (responseType === "blob") {
        throw await convertBlobToText(err);
      } else {
        throw err;
      }
    } finally {
      span.end();
    }
  });
};

export const patchCall = async (url, data, params = {}, errorCallback = "") => {
  const tracer = trace.getTracer("oms-ui");
  const span = tracer.startSpan(`patchCall:${url}`, {
    attributes: { "http.url": url, "http.method": "PATCH" },
  });

  return await context.with(trace.setSpan(context.active(), span), async () => {
    try {
      const response = await axios.patch(url, data, {
        params,
      });
      span.setStatus({ code: SpanStatusCode.OK });
      return response.data;
    } catch (err) {
      span.recordException(err);
      span.setStatus({
        code: SpanStatusCode.ERROR,
        message: err.message,
      });
      errorCallback && errorCallback(err?.response);
    } finally {
      span.end();
    }
  });
};

export const deleteCall = async (
  url,
  data,
  params = {},
  errorCallback = "",
) => {
  const tracer = trace.getTracer("oms-ui");
  const span = tracer.startSpan(`deleteCall:${url}`, {
    attributes: { "http.url": url, "http.method": "DELETE" },
  });

  return await context.with(trace.setSpan(context.active(), span), async () => {
    try {
      const response = await axios.delete(url, {
        data,
        params,
      });
      span.setStatus({ code: SpanStatusCode.OK });
      return response.data;
    } catch (err) {
      span.recordException(err);
      span.setStatus({
        code: SpanStatusCode.ERROR,
        message: err.message,
      });
      errorCallback && errorCallback(err?.response);
    } finally {
      span.end();
    }
  });
};

export const deleteCall_v2 = async (url, data, params = {}) => {
  const tracer = trace.getTracer("oms-ui");
  const span = tracer.startSpan(`deleteCall_v2:${url}`, {
    attributes: { "http.url": url, "http.method": "DELETE" },
  });

  return await context.with(trace.setSpan(context.active(), span), async () => {
    try {
      const response = await axios.delete(url, {
        data,
        params,
      });
      span.setStatus({ code: SpanStatusCode.OK });
      return response;
    } catch (error) {
      span.recordException(error);
      span.setStatus({
        code: SpanStatusCode.ERROR,
        message: error.message,
      });
      throw error;
    } finally {
      span.end();
    }
  });
};

export const postCall_v2 = async (
  url,
  data,
  params = {},
  config = {},
  abortKey = url,
) => {
  const controller = createAbortController(abortKey);
  const tracer = trace.getTracer("oms-ui");
  const span = tracer.startSpan(`postCall_v2:${url}`, {
    attributes: { "http.url": url, "http.method": "POST" },
  });

  return await context.with(trace.setSpan(context.active(), span), async () => {
    try {
      const response = await axios
        .post(url, data, { params, ...config, signal: controller.signal })
        .catch((err) => {
          if (controller.signal.aborted) return null;
          throw err;
        });

      if (response === null) return;

      span.setStatus({ code: SpanStatusCode.OK });
      return response;
    } catch (error) {
      span.recordException(error);
      span.setStatus({ code: SpanStatusCode.ERROR, message: error?.message });
      throw error;
    } finally {
      cleanupAbortController(abortKey, controller);
      span.end();
    }
  });
};

export const putCall_v2 = async (url, data, params = {}) => {
  const tracer = trace.getTracer("oms-ui");
  const span = tracer.startSpan(`putCall_v2:${url}`, {
    attributes: { "http.url": url, "http.method": "PUT" },
  });

  return await context.with(trace.setSpan(context.active(), span), async () => {
    try {
      const response = await axios.put(url, data, {
        params,
      });
      span.setStatus({ code: SpanStatusCode.OK });
      return response.data;
    } catch (err) {
      span.recordException(err);
      span.setStatus({
        code: SpanStatusCode.ERROR,
        message: err.message,
      });
      throw err;
    } finally {
      span.end();
    }
  });
};
